import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Users, Package, FileCheck, ExternalLink, AlertTriangle, Truck } from "lucide-react";

export default function AdminPanel() {
  const [stats, setStats] = useState({});
  const [docs, setDocs] = useState([]);
  const [users, setUsers] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [reviewQueue, setReviewQueue] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [tab, setTab] = useState("docs");

  const load = async () => {
    try {
      const [s, d, u, dis, review, driverList] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/documents"),
        api.get("/admin/users"),
        api.get("/admin/disputes"),
        api.get("/admin/review-queue").catch(() => ({ data: [] })),
        api.get("/admin/drivers").catch(() => ({ data: [] })),
      ]);
      setStats(s.data); setDocs(d.data); setUsers(u.data); setDisputes(dis.data);
      setReviewQueue(review.data || []); setDrivers(driverList.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const validate = async (docId, status) => {
    const reason = status === "rejected" ? prompt("Motif de refus ?") : null;
    if (status === "rejected" && reason === null) return;
    try {
      await api.post(`/admin/documents/${docId}/validate`, { status, reason });
      toast.success(`Document ${status === "validated" ? "validé" : "refusé"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const viewDoc = async (docId) => {
    try {
      const { data } = await api.get(`/documents/${docId}`);
      if (!data.file_data) {
        toast.error("Document sans fichier");
        return;
      }
      // Convert data URL to Blob and open via Blob URL.
      // Pourquoi : document.write() avec une data URL interpolée crée un vecteur XSS
      // si l'image était piégée. Le rendu via Blob URL ne s'exécute pas comme du HTML.
      const [meta, b64] = data.file_data.split(",", 2);
      const mime = (meta.match(/data:([^;]+)/) || [])[1] || "application/octet-stream";
      const binary = atob(b64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: mime });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank", "noopener,noreferrer");
      // Rétention temporaire de l'URL pour permettre au navigateur de la charger
      // avant la révocation. 60s est largement suffisant.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      if (!w) toast.error("Popup bloqué par le navigateur — autorisez les popups pour ce site");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resolveDispute = async (disputeId, decision, refundAmount) => {
    const notes = prompt("Notes admin (motif de la décision, visible par les 2 parties) :");
    if (notes === null) return;
    try {
      await api.post(`/admin/disputes/${disputeId}/resolve`, {
        decision,
        refund_amount: refundAmount,
        admin_notes: notes,
      });
      toast.success("Litige tranché");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setDriverStatus = async (driverId, status) => {
    const reason = status === "rejected" ? prompt("Motif de refus ?") : "";
    if (status === "rejected" && reason === null) return;
    try {
      await api.patch(`/admin/drivers/${driverId}/status`, { status, reason });
      toast.success("Statut conducteur mis à jour");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="admin-panel">
      <div className="overline text-accent mb-2">— Admin</div>
      <h1 className="font-display font-bold text-4xl tracking-tighter mb-8">Panneau d&apos;administration</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#1F4032]/10 border border-[#1F4032]/10 mb-8">
        {[
          ["Utilisateurs", stats.users_total, Users],
          ["Transporteurs validés", `${stats.transporters_validated || 0}/${stats.transporters || 0}`, FileCheck],
          ["Missions livrées", stats.missions_delivered, Package],
          ["Litiges actifs", disputes.length, AlertTriangle],
        ].map(([l, v, Icon]) => (
          <div key={l} className="bg-white p-5">
            <Icon className="w-4 h-4 text-muted-foreground mb-2" />
            <div className="overline text-muted-foreground">{l}</div>
            <div className="font-display font-bold text-3xl mt-1">{v ?? 0}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-0 border border-[#1F4032] mb-6">
        {[["docs", "Documents"], ["users", "Utilisateurs"], ["disputes", `Litiges${disputes.length ? ` (${disputes.length})` : ""}`], ["review", `Revues${reviewQueue.length ? ` (${reviewQueue.length})` : ""}`], ["drivers", "Conducteurs"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-5 py-3 text-sm font-medium border-r border-[#1F4032] last:border-0 ${tab === k ? "bg-[#1F4032] text-white" : "bg-white hover:bg-[#1F4032]/5"}`}
            data-testid={`admin-tab-${k}`}>
            {l.toUpperCase()}
          </button>
        ))}
      </div>

      {tab === "docs" && (
        <div className="border border-[#1F4032]/10 rounded-md" data-testid="admin-docs">
          {docs.length === 0 && <div className="p-6 text-muted-foreground">Aucun document.</div>}
          {docs.map((d) => (
            <div key={d.id} className="flex flex-wrap items-start justify-between gap-3 p-4 border-b border-[#1F4032]/10 last:border-0">
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold flex items-center gap-2 flex-wrap">
                  {d.doc_type}
                  {d.license_category && (
                    <span className="text-[10px] font-mono bg-neutral-100 px-2 py-0.5 rounded">
                      Permis {d.license_category}
                    </span>
                  )}
                  {d.captured_from_camera && (
                    <span className="text-[10px] font-mono bg-blue-100 text-blue-700 px-2 py-0.5 rounded">📷 CAM recto</span>
                  )}
                  {d.captured_from_camera_back && (
                    <span className="text-[10px] font-mono bg-blue-100 text-blue-700 px-2 py-0.5 rounded">📷 CAM verso</span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {d.user?.name} · {d.user?.email} · {d.uploaded_at?.slice(0, 16).replace("T", " ")}
                </div>
                {(d.file_hash || d.file_name) && (
                  <div className="mt-2 text-[11px] font-mono bg-neutral-50 p-2 rounded border border-neutral-200 space-y-0.5 overflow-hidden">
                    {d.file_name && <div>📄 <span className="text-neutral-700">{d.file_name}</span> · {d.file_size ? `${(d.file_size/1024).toFixed(1)} Ko` : ""}</div>}
                    {d.file_last_modified && <div>🕑 <span className="text-neutral-600">Modifié: {new Date(d.file_last_modified).toLocaleString("fr-FR")}</span></div>}
                    {d.file_hash && <div className="truncate" title={d.file_hash}>🔐 SHA-256: <span className="text-neutral-500">{d.file_hash.slice(0, 24)}…</span></div>}
                    {d.file_hash_back && <div className="truncate" title={d.file_hash_back}>🔐 Verso: <span className="text-neutral-500">{d.file_hash_back.slice(0, 24)}…</span></div>}
                  </div>
                )}
                {d.doc_type === "selfie" && typeof d.face_match !== "undefined" && (
                  <div className={`mt-2 p-2 rounded border text-xs ${
                    d.face_match === true ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                    : d.face_match === false ? "bg-red-50 border-red-200 text-red-900"
                    : "bg-amber-50 border-amber-200 text-amber-900"
                  }`}>
                    <div className="font-semibold">
                      Pré-analyse ({d.face_model || "modèle configuré"}) —{" "}
                      {d.face_match === true ? `✓ Match ${d.face_confidence}%`
                        : d.face_match === false ? `✗ Pas de match (${d.face_confidence}%)`
                        : "Analyse impossible"}
                    </div>
                    {d.face_reason && <div className="mt-1 opacity-90">{d.face_reason}</div>}
                  </div>
                )}
                {d.note && <div className="text-xs mt-1">Note: {d.note}</div>}
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 text-xs font-mono text-white rounded ${d.status === "validated" ? "bg-emerald-600" : d.status === "rejected" ? "bg-red-600" : "bg-amber-500"}`}>
                  {d.status.toUpperCase()}
                </span>
                <Button size="sm" variant="outline" className="rounded-full border-neutral-300 gap-1"
                  onClick={() => viewDoc(d.id)}>
                  <ExternalLink className="w-3 h-3" /> Voir
                </Button>
                {d.status === "pending" && (
                  <>
                    <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => validate(d.id, "validated")}
                      data-testid={`validate-${d.id}`}>Valider</Button>
                    <Button size="sm" className="rounded-full bg-red-600 hover:bg-red-700 text-white"
                      onClick={() => validate(d.id, "rejected")}
                      data-testid={`reject-${d.id}`}>Refuser</Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "disputes" && (
        <div className="border border-[#1F4032]/10" data-testid="admin-disputes">
          {disputes.length === 0 && <div className="p-6 text-muted-foreground">Aucun litige actif.</div>}
          {disputes.map((d) => (
            <div key={d.id} className="p-4 border-b border-[#1F4032]/10 last:border-0 space-y-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${d.status === "open" ? "bg-amber-500 text-white" : "bg-blue-600 text-white"}`}>
                      {d.status.toUpperCase()}
                    </span>
                    <span className="font-display font-bold">{d.type}</span>
                    <span className="text-xs text-neutral-500 font-mono">
                      Mission {d.mission_id.slice(0, 8)}
                    </span>
                  </div>
                  <div className="text-xs text-neutral-500 mt-1">
                    Ouvert par {d.opened_side} · le {new Date(d.created_at).toLocaleString("fr-FR")}
                  </div>
                  {d.amount_contested && (
                    <div className="text-xs mt-1">Montant demandé : <b>{d.amount_contested}€</b></div>
                  )}
                </div>
                <a href={`/missions/${d.mission_id}`} target="_blank" rel="noopener noreferrer"
                  className="text-xs text-[#1F4032] hover:underline flex items-center gap-1">
                  Voir mission <ExternalLink className="w-3 h-3" />
                </a>
              </div>

              <div className="space-y-2 pl-2 border-l-2 border-amber-300">
                {(d.timeline || []).map((e, i) => (
                  <div key={`${e.at}-${e.side}-${i}`} className="text-xs bg-neutral-50 p-2 rounded">
                    <div className="font-mono uppercase text-neutral-600">
                      {e.side} · {new Date(e.at).toLocaleString("fr-FR")}
                    </div>
                    <div className="mt-1 whitespace-pre-wrap">{e.message}</div>
                    {(e.photos || []).length > 0 && (
                      <div className="flex gap-1 mt-1">
                        {e.photos.map((p, j) => (
                          <a key={j} href={p} target="_blank" rel="noopener noreferrer"
                            className="block w-12 h-12 rounded overflow-hidden border border-neutral-200">
                            <img src={p} alt={`document ${j + 1}`} className="w-full h-full object-cover" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-2 pt-2">
                <Button size="sm" onClick={() => resolveDispute(d.id, "validate_transporter")}
                  className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid={`resolve-validate-${d.id}`}>
                  ✓ Valider transporteur
                </Button>
                <Button size="sm" onClick={() => resolveDispute(d.id, "refund_full")}
                  className="rounded-full bg-red-600 hover:bg-red-700 text-white"
                  data-testid={`resolve-refund-full-${d.id}`}>
                  ↩ Refund total expéditeur
                </Button>
                <Button size="sm" variant="outline" onClick={() => {
                  const amt = prompt("Montant à rembourser (€) — partiel :");
                  const v = parseFloat(amt);
                  if (!v || v <= 0) return;
                  resolveDispute(d.id, "refund_partial", v);
                }}
                  className="rounded-full border-amber-500 text-amber-700 hover:bg-amber-500 hover:text-white"
                  data-testid={`resolve-partial-${d.id}`}>
                  ½ Refund partiel
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "users" && (
        <div className="border border-[#1F4032]/10" data-testid="admin-users">
          {users.map((u) => (
            <div key={u.id} className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-[#1F4032]/10 last:border-0">
              <div>
                <div className="font-display font-bold">{u.name}</div>
                <div className="text-xs text-muted-foreground">{u.email} · {u.phone || "—"}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="overline">{u.role}</span>
                <span className={`px-2 py-1 text-xs font-mono ${u.kyc_status === "validated" ? "bg-green-600 text-white" : u.kyc_status === "pending" ? "bg-amber-500 text-white" : "bg-muted"}`}>
                  KYC: {u.kyc_status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "review" && (
        <div className="border border-[#1F4032]/10 rounded-md" data-testid="admin-review-queue">
          {reviewQueue.length === 0 && <div className="p-6 text-muted-foreground">Aucune mission en attente de revue humaine.</div>}
          {reviewQueue.map((m) => (
            <div key={m.id} className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-[#1F4032]/10 last:border-0">
              <div><div className="font-display font-bold">{m.title || "Mission sans titre"}</div><div className="text-xs text-neutral-500">{m.id?.slice(0, 8)} · {m.status} · revue depuis {m.human_review_due_at ? new Date(m.human_review_due_at).toLocaleString("fr-FR") : "—"}</div></div>
              <a href={`/missions/${m.id}`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#1F4032] underline flex items-center gap-1">Ouvrir <ExternalLink className="w-3 h-3" /></a>
            </div>
          ))}
        </div>
      )}

      {tab === "drivers" && (
        <div className="border border-[#1F4032]/10 rounded-md" data-testid="admin-drivers">
          {drivers.length === 0 && <div className="p-6 text-muted-foreground">Aucun conducteur déclaré.</div>}
          {drivers.map((d) => (
            <div key={d.id} className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-[#1F4032]/10 last:border-0">
              <div><div className="font-display font-bold flex items-center gap-2"><Truck className="w-4 h-4" />{d.name}</div><div className="text-xs text-neutral-500">Permis {d.license_category || "—"} · entreprise {d.transporter_id?.slice(0, 8)}</div></div>
              <div className="flex items-center gap-2"><span className={`px-2 py-1 text-xs rounded ${d.status === "validated" ? "bg-emerald-100 text-emerald-800" : d.status === "rejected" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>{d.status}</span><select value={d.status || "pending"} onChange={(e) => setDriverStatus(d.id, e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm"><option value="pending">En attente</option><option value="validated">Valider</option><option value="rejected">Refuser</option></select></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
